// src/pages/IntelligencePage.js
import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { formatDate } from '../utils/constants';

const TIER_CONFIG = {
  T3: { label: 'Construction Intel', color: 'var(--blue)',   desc: 'Upcoming projects, pre-tender signals' },
  T4: { label: 'Indigenous',         color: 'var(--purple)', desc: 'Partnership and procurement opportunities' },
  T5: { label: 'Industry News',      color: '#8b949e',       desc: 'Mining, oil & gas, construction news' },
};

const SECTOR_COLORS = {
  underground_mining:   'var(--accent)',
  oil_gas:              'var(--red)',
  heavy_industrial:     'var(--blue)',
  consulting:           'var(--blue)',
  indigenous_partnership: 'var(--purple)',
  decommissioning:      'var(--orange)',
  other:                'var(--text-muted)',
};

export default function IntelligencePage() {
  const [items, setItems]   = useState([]);
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ tier: '', is_read: '', is_starred: '', search: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.tier)      params.set('tier', filters.tier);
      if (filters.is_read)   params.set('is_read', filters.is_read);
      if (filters.is_starred)params.set('is_starred', filters.is_starred);
      if (filters.search)    params.set('search', filters.search);
      params.set('limit', '100');
      const data = await api.get(`/intelligence?${params}`);
      setItems(data.items || []);
      setCounts(data.counts || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const totalUnread = counts.reduce((a, c) => a + (parseInt(c.unread) || 0), 0);
  const totalItems  = counts.reduce((a, c) => a + (parseInt(c.total) || 0), 0);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Intelligence Feed</div>
          <div style={{ fontSize:'0.85rem', color:'var(--text-muted)', marginTop:'0.2rem' }}>
            {totalItems} items — {totalUnread > 0 && <span style={{ color:'var(--accent)' }}>{totalUnread} unread</span>}
          </div>
        </div>
      </div>

      {/* Tier filter pills */}
      <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1rem', flexWrap:'wrap' }}>
        <button onClick={() => setFilters(f => ({ ...f, tier: '' }))}
          style={{ padding:'0.3rem 0.75rem', borderRadius:20, border:'1px solid', fontSize:'0.78rem', cursor:'pointer',
            borderColor: !filters.tier ? 'var(--accent)' : 'var(--border)',
            background: !filters.tier ? 'var(--accent-dim)' : 'transparent',
            color: !filters.tier ? 'var(--accent)' : 'var(--text-secondary)',
          }}>
          All {totalItems > 0 && `(${totalItems})`}
        </button>
        {Object.entries(TIER_CONFIG).map(([tier, cfg]) => {
          const count = counts.find(c => c.tier === tier);
          const isActive = filters.tier === tier;
          return (
            <button key={tier} onClick={() => setFilters(f => ({ ...f, tier: isActive ? '' : tier }))}
              style={{ padding:'0.3rem 0.75rem', borderRadius:20, border:'1px solid', fontSize:'0.78rem', cursor:'pointer',
                borderColor: isActive ? cfg.color : 'var(--border)',
                background: isActive ? cfg.color + '22' : 'transparent',
                color: isActive ? cfg.color : 'var(--text-secondary)',
              }}>
              {tier} {cfg.label} {count && `(${count.total})`}
              {count && parseInt(count.unread) > 0 && (
                <span style={{ marginLeft:4, background:cfg.color, color:'#fff', borderRadius:8, padding:'0 4px', fontSize:'0.65rem' }}>
                  {count.unread}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:'0.75rem', marginBottom:'1.25rem', flexWrap:'wrap', alignItems:'center' }}>
        <input type="text" placeholder="Search intelligence…" value={filters.search}
          onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} style={{ maxWidth:280 }} />
        <label style={{ display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.82rem', color:'var(--text-secondary)', cursor:'pointer' }}>
          <input type="checkbox" checked={filters.is_starred === '1'}
            onChange={e => setFilters(f => ({ ...f, is_starred: e.target.checked ? '1' : '' }))} />
          Starred only
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.82rem', color:'var(--text-secondary)', cursor:'pointer' }}>
          <input type="checkbox" checked={filters.is_read === '0'}
            onChange={e => setFilters(f => ({ ...f, is_read: e.target.checked ? '0' : '' }))} />
          Unread only
        </label>
      </div>

      {/* Feed */}
      {loading ? (
        <div style={{ padding:'3rem', textAlign:'center', color:'var(--text-muted)' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <RadarIcon />
          <h3>No intelligence items yet</h3>
          <p style={{ fontSize:'0.85rem' }}>Run the tiered scraper to populate the intelligence feed.</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {items.map(item => {
            const tierCfg = TIER_CONFIG[item.tier] || {};
            const sectorColor = SECTOR_COLORS[item.sector] || 'var(--text-muted)';
            return (
              <div key={item.id} className="card" style={{
                padding:'0.875rem 1rem',
                borderLeft:`3px solid ${tierCfg.color || 'var(--border)'}`,
                opacity: item.is_read ? 0.7 : 1,
              }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:'0.75rem' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.3rem', flexWrap:'wrap' }}>
                      <span style={{ fontSize:'0.65rem', fontWeight:700, color: tierCfg.color, background: (tierCfg.color||'') + '22', padding:'0.1rem 0.45rem', borderRadius:8 }}>
                        {item.tier}
                      </span>
                      <span style={{ fontSize:'0.7rem', color:'var(--text-muted)' }}>{item.source_name}</span>
                      {item.sector && item.sector !== 'other' && (
                        <span style={{ fontSize:'0.68rem', color: sectorColor, fontWeight:600 }}>
                          {item.sector.replace('_',' ')}
                        </span>
                      )}
                      {item.is_starred ? <span style={{ color:'var(--accent)' }}>★</span> : null}
                      <span style={{ marginLeft:'auto', fontSize:'0.72rem', color:'var(--text-muted)' }}>
                        {formatDate(item.published_date)}
                      </span>
                    </div>

                    <div style={{ fontWeight: item.is_read ? 400 : 600, fontSize:'0.875rem', marginBottom:'0.3rem', lineHeight:1.4 }}>
                      {item.url ? (
                        <a href={item.url} target="_blank" rel="noreferrer" style={{ color:'var(--text-primary)', textDecoration:'none' }}>
                          {item.title}
                        </a>
                      ) : item.title}
                    </div>

                    {item.description && (
                      <div style={{ fontSize:'0.8rem', color:'var(--text-muted)', lineHeight:1.5 }}>
                        {item.description.slice(0, 200)}{item.description.length > 200 ? '…' : ''}
                      </div>
                    )}
                  </div>

                  <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem', flexShrink:0 }}>
                    {item.url && (
                      <a href={item.url} target="_blank" rel="noreferrer"
                        className="btn btn-secondary" style={{ padding:'0.3rem 0.6rem', fontSize:'0.75rem' }}>
                        View ↗
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RadarIcon() {
  return <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="2"/><path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14"/></svg>;
}
